import geojson from 'geojson';
import * as I from 'immutable';
import mapboxgl from 'mapbox-gl';
import React from 'react';

import Map from 'app/components/DeclarativeMap';
import DrawLayer, {DrawingMode} from 'app/components/DeclarativeMap/DrawLayer';
import FeatureCollectionVector from 'app/components/DeclarativeMap/FeatureCollectionVector';
import GeoJsonFitter from 'app/components/DeclarativeMap/GeoJsonFitter';
import GeoJsonLayersVector from 'app/components/DeclarativeMap/GeoJsonLayersVector';
import {GeometryOverlayContent} from 'app/components/DeclarativeMap/GeometryOverlaysContent';
import MapContent from 'app/components/DeclarativeMap/MapContent';
import MapGeocoderControl, {GeocoderResult} from 'app/components/DeclarativeMap/MapGeocoderControl';
import MapHelptext from 'app/components/DeclarativeMap/MapHelptext';
import MapLegend from 'app/components/DeclarativeMap/MapLegend';
import MapNavigationControl from 'app/components/DeclarativeMap/MapNavigationControl';
import MapStyle from 'app/components/DeclarativeMap/MapStyle';
import MapStyleControl from 'app/components/DeclarativeMap/StyleControl';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {
  GeometryOverlaySetting,
  HydratedFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {MeasurementSystem} from 'app/modules/Remote/Organization';
import colors from 'app/styles/colors.json';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as mapUtils from 'app/utils/mapUtils';

import {NumberedFeature} from './ManageFeaturesProvider';
import {BLANK_HYDRATED_FEATURE_COLLECTION} from './ManageFeaturesView';
import cs from './styles.module.scss';

import {ManagePropertiesMode} from '.';

const NEW_PROPERTIES_COLOR = colors.darkOrange;
const EXISTING_PROPERTIES_COLOR = colors.brightYellow;
const SPLIT_ORIGINAL_OUTLINE_COLOR = '#ffffff';

// Muted fill opacity for this view — features here are upload previews, not
// the user's primary content, so dimming the fills lets the white original
// outline and the boundary lines read more clearly.
const FILL_OPACITY_SCALE = 0.5;

type AllowsDrawing =
  | {
      allowDrawing: true;
      isEditMode: boolean;
      featureToEdit: null | geojson.Feature;
      setFeatureToEdit: React.Dispatch<React.SetStateAction<geojson.Feature | null>>;
      setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
      onMapFeatureClick?: (
        f: geojson.Feature<geojson.Polygon | geojson.MultiPolygon>,
        ev: mapboxgl.MapLayerMouseEvent
      ) => void;
      drawingMode?: DrawingMode;
      selectedOverlayFc?: HydratedFeatureCollection | null;
    }
  | {
      allowDrawing?: false;
      isEditMode?: undefined;
      featureToEdit?: undefined;
      setFeatureToEdit?: undefined;
      setIsEditMode?: undefined;
      onMapFeatureClick?: undefined;
      drawingMode?: undefined;
      selectedOverlayFc?: undefined;
    };

// In updateOverlay mode the map renders the existing overlay's feature collection;
// in every other mode it renders the existing portfolio's feature collection. The
// two are mutually exclusive, so callers pass exactly one based on `mode`.
type ExistingFcByMode =
  | {
      mode: 'updateOverlay';
      existingOverlayFeatureCollection: HydratedFeatureCollection | null;
      existingFeatureCollection?: never;
    }
  | {
      mode: Exclude<ManagePropertiesMode, 'updateOverlay'>;
      existingFeatureCollection: HydratedFeatureCollection | null | undefined;
      existingOverlayFeatureCollection?: never;
    };

export type Props = AllowsDrawing &
  ExistingFcByMode & {
    newFeatureCollection: HydratedFeatureCollection | null;
    hoveredFeatureIds: I.Set<number>;
    hoverFeatureById: (fId: number | null) => void;
    selectedFeature: NumberedFeature | null;
    measurementSystem: MeasurementSystem;
    overlaySetting?: GeometryOverlaySetting;
    firebaseToken: string | null;
    /**
     * Geometries rendered as a white outline (no fill): pre-split originals
     * plus any currently-oversized features the user hasn't fixed yet. Included
     * in the map's fit-bounds so the camera moves to them even when they're
     * excluded from the main new-properties layer.
     */
    outlineGeometries?: geojson.Geometry[];
  };

const ManageFeaturesMap: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  existingFeatureCollection,
  existingOverlayFeatureCollection,
  newFeatureCollection,
  selectedFeature,
  hoveredFeatureIds,
  hoverFeatureById,
  featureToEdit,
  setFeatureToEdit,
  isEditMode,
  setIsEditMode,
  mode,
  allowDrawing = false,
  measurementSystem,
  overlaySetting,
  firebaseToken,
  onMapFeatureClick,
  drawingMode = 'draw_polygon',
  selectedOverlayFc,
  outlineGeometries = [],
}) => {
  const [mapStyle, setMapStyle] = React.useState<mapUtils.MapStyle>(
    allowDrawing ? mapUtils.MAPBOX_SATELLITE_STREETS_STYLE : mapUtils.MAPBOX_DARK_STYLE
  );
  const [geocoderResult, setGeocoderResult] = React.useState<GeocoderResult | null>(null);

  const EMPTY_NEW_FEATURE_COLLECTION = BLANK_HYDRATED_FEATURE_COLLECTION.set('id', 1);
  const EMPTY_EXISTING_FEATURE_COLLECTION = BLANK_HYDRATED_FEATURE_COLLECTION.set('id', 2);

  const newFeatures = React.useMemo(
    () =>
      newFeatureCollection
        ? geoJsonUtils.featureCollection(newFeatureCollection.get('features').toJS())
        : null,
    [newFeatureCollection]
  );

  const existingFeatures: geojson.FeatureCollection | null = React.useMemo(
    () =>
      existingFeatureCollection
        ? geoJsonUtils.featureCollection(existingFeatureCollection.get('features').toJS())
        : null,
    [existingFeatureCollection]
  );

  const outlineFC: geojson.FeatureCollection | null = React.useMemo(
    () =>
      outlineGeometries.length > 0
        ? {
            type: 'FeatureCollection',
            features: outlineGeometries.map(
              (geometry): geojson.Feature => ({
                type: 'Feature',
                properties: null,
                geometry,
              })
            ),
          }
        : null,
    [outlineGeometries]
  );

  // Was the query all coordinate?
  // Notes: we may have coords separated by comma or space or some combination
  const matchedQuery = geocoderResult && geocoderResult.query.match(/([-0-9.]+)[ ,]+([-0-9.]+)/);

  // The geocoder result may have a point or a bbox, but we may
  // want to use the coordinates in the query, if we matched any.
  const geocoderGeometry = React.useMemo(() => {
    if (!geocoderResult) {
      return null;
    }

    // If we have a matched query, set as a point. If we have a bbox, use the polygon. Otherwise, use result geom.
    switch (true) {
      case !!matchedQuery:
        return {type: 'Point', coordinates: [matchedQuery[2], matchedQuery[1]]};
      case !!geocoderResult.result.bbox:
        return {type: 'Polygon', coordinates: geocoderResult.result.bbox};
      default:
        return geocoderResult.result.geometry;
    }
  }, [geocoderResult]);

  // Combine the new-properties FC and the outline (pre-split + currently-
  // oversized) FC for map-fitting purposes — without this, an upload made
  // entirely of oversized features would leave the camera where it was.
  // GeoJsonFitter only reads geometry/bbox, so the properties type doesn't
  // matter here — widen to `unknown` to merge cleanly with newFeatures (which
  // also uses `unknown` via geoJsonUtils.featureCollection).
  const newAndOutlineFC: geojson.FeatureCollection<geojson.Geometry, unknown> | null =
    React.useMemo(() => {
      const features: geojson.Feature<geojson.Geometry, unknown>[] = [
        ...(newFeatures?.features ?? []),
        ...(outlineFC?.features ?? []),
      ];
      // Null (not empty FC) is load-bearing: GeoJsonFitter's bbox(emptyFC)
      // returns Infinity bounds and would crash/zoom-to-world on fitBounds.
      return features.length > 0 ? {type: 'FeatureCollection', features} : null;
    }, [newFeatures, outlineFC]);

  // Feature(s) for the GeoJsonFitter to focus the map on. A geocoder result takes precedence,
  // followed by the selected feature, the newly uploaded collection (which now also covers
  // oversized outlines), the pre-existing collection, and finally — in updateOverlay mode — the
  // bounds polygon from the existing overlay's ApiFeatureCollection (used when the upload hasn't
  // happened yet and the existing overlay is tile-based, so no in-memory features are available
  // to fit to).
  const fitterFeatures = (geocoderGeometry ||
    selectedFeature ||
    newAndOutlineFC ||
    existingFeatures ||
    (existingOverlayFeatureCollection
      ? (existingOverlayFeatureCollection.get('bounds')?.toJS() ?? null)
      : null)) as unknown as geojson.GeoJSON | null;

  // Once we move away from a geocoder result and focus another feature, we want
  // to clear it, so that We don't refit the map to an old search location.
  React.useEffect(() => {
    setGeocoderResult(null);
  }, [selectedFeature, newAndOutlineFC, existingFeatures]);

  return (
    <div className={cs.map}>
      <MapHelptext hide={!(isEditMode && allowDrawing)} top={'2rem'}>
        <div className={cs.mapHelpText}>
          {isEditMode && allowDrawing && (
            <>
              <div>Click on the map to start drawing a property.</div>
              {drawingMode === 'draw_polygon' && (
                <div>Double-click or press enter to complete.</div>
              )}
              {drawingMode === 'draw_rectangle' && <div>Click again to complete.</div>}
            </>
          )}
        </div>
      </MapHelptext>
      {/* hide the navigationControl that gets added to DeclarativeMap-- we will
      render it separately below with the MapNavigationControl component to allow
      us to conditionally show/hide it */}
      <Map
        firebaseToken={firebaseToken}
        controlPosition="top-right"
        hideNavigationControl
        // Disabiling boxZoom allows us to use shift+click for the parcel data interactions
        // otherwise mapbox's boxZoom handler catches those events and they don't make it to
        // our event handler
        mapOptions={{boxZoom: false}}
      >
        {(map, isMapLoaded) => {
          const layers: {color: string; name: string}[] = [];
          if (mode === 'updateOverlay') {
            if (existingOverlayFeatureCollection) {
              layers.push({color: EXISTING_PROPERTIES_COLOR, name: 'Current overlay'});
            }
            if (newFeatureCollection) {
              layers.push({color: NEW_PROPERTIES_COLOR, name: 'Updated overlay'});
            }
          } else {
            if (existingFeatureCollection) {
              layers.push({color: EXISTING_PROPERTIES_COLOR, name: 'Existing properties'});
            }
            if (newFeatureCollection) {
              layers.push({
                color: NEW_PROPERTIES_COLOR,
                name: mode === 'overlay' ? 'New overlay' : 'New properties',
              });
            }
            if (outlineFC) {
              layers.push({color: SPLIT_ORIGINAL_OUTLINE_COLOR, name: 'Oversized feature (original)'});
            }
          }
          return (
            <React.Fragment>
              <MapStyle map={map} style={mapStyle} />
              {!isEditMode && (
                <>
                  <MapNavigationControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    position={'top-right'}
                  />
                  <MapStyleControl
                    map={map}
                    isMapLoaded={isMapLoaded}
                    styles={mapUtils.MAPBOX_STYLES}
                    onChangeMapStyle={setMapStyle}
                    selectedStyle={mapStyle}
                    position={'top-right'}
                  />
                </>
              )}
              <MapContent
                map={map}
                isMapLoaded={isMapLoaded}
                controls={[new mapboxgl.ScaleControl({unit: measurementSystem})]}
                controlPosition={'bottom-left'}
              />
              {allowDrawing && !isEditMode && (
                <MapGeocoderControl
                  map={map}
                  isMapLoaded={isMapLoaded}
                  position={'top-left'}
                  onResult={setGeocoderResult}
                />
              )}
              {(existingFeatureCollection ||
                existingOverlayFeatureCollection ||
                newFeatureCollection) &&
                !isEditMode && <MapLegend layers={layers} className={cs.mapLegend} />}
              {existingOverlayFeatureCollection && (
                <GeometryOverlayContent
                  key={existingOverlayFeatureCollection.get('id')}
                  map={map}
                  isMapLoaded={isMapLoaded}
                  color={EXISTING_PROPERTIES_COLOR}
                  hydratedFeatureCollection={existingOverlayFeatureCollection}
                  onClick={() => {}}
                  isVisible={true}
                  defaultFilled={true}
                  defaultUnfilled={false}
                />
              )}
              {mode === 'parcel' && selectedOverlayFc && onMapFeatureClick && (
                <GeometryOverlayContent
                  key={selectedOverlayFc.get('id')}
                  map={map}
                  isMapLoaded={isMapLoaded}
                  color={overlaySetting?.color || NEW_PROPERTIES_COLOR}
                  hydratedFeatureCollection={selectedOverlayFc}
                  onClick={(
                    f: I.ImmutableOf<ApiFeature>,
                    _fc: HydratedFeatureCollection,
                    ev: mapboxgl.MapLayerMouseEvent
                  ) => {
                    if (!isEditMode && f) {
                      onMapFeatureClick(f.toJS(), ev);
                    }
                  }}
                  isVisible={true}
                  defaultFilled={true}
                  defaultUnfilled={false}
                  hoverFillFeatureOpacity={0.5}
                  fillOpacityOverride={0.01}
                  pinOnClick={false}
                />
              )}
              {/* Dont need this for overlays */}
              {allowDrawing &&
                setFeatureToEdit &&
                isEditMode &&
                setIsEditMode &&
                featureToEdit !== undefined && (
                  <DrawLayer
                    map={map}
                    isMapLoaded={isMapLoaded}
                    visible={true}
                    drawingMode={drawingMode}
                    feature={featureToEdit}
                    setFeature={setFeatureToEdit}
                    onDeselectFeature={() => setIsEditMode(false)}
                  />
                )}
              <GeoJsonFitter
                map={map}
                geoJson={fitterFeatures}
                fitBoundsOptions={{animate: false, maxZoom: 17}}
                geoJsonComparison={'shallow'}
              />
              {(mode === 'overlay' || mode === 'updateOverlay') && newFeatureCollection ? (
                <GeometryOverlayContent
                  map={map}
                  isMapLoaded={isMapLoaded}
                  color={overlaySetting?.color || NEW_PROPERTIES_COLOR}
                  hydratedFeatureCollection={newFeatureCollection}
                  onClick={() => {}}
                  isVisible={true}
                  defaultFilled={overlaySetting?.defaultFilled || true}
                  defaultUnfilled={overlaySetting?.defaultUnfilled || false}
                />
              ) : (
                <>
                  <FeatureCollectionVector
                    map={map}
                    isMapLoaded={isMapLoaded}
                    featureCollection={newFeatureCollection ?? EMPTY_NEW_FEATURE_COLLECTION}
                    selectedFeatureIds={I.Set([])}
                    selectFeatureById={() => null}
                    hoveredFeatureIds={hoveredFeatureIds}
                    hoverFeatureById={hoverFeatureById}
                    labelOnHover={true}
                    customColor={NEW_PROPERTIES_COLOR}
                    fillOpacityScale={FILL_OPACITY_SCALE}
                    // make sure new properties are always on top
                    sendToTop={true}
                    mode="portfolio"
                  />

                  <FeatureCollectionVector
                    map={map}
                    isMapLoaded={isMapLoaded}
                    featureCollection={
                      existingFeatureCollection ?? EMPTY_EXISTING_FEATURE_COLLECTION
                    }
                    selectedFeatureIds={I.Set([])}
                    selectFeatureById={() => null}
                    hoveredFeatureIds={hoveredFeatureIds}
                    hoverFeatureById={hoverFeatureById}
                    labelOnHover={true}
                    customColor={EXISTING_PROPERTIES_COLOR}
                    fillOpacityScale={FILL_OPACITY_SCALE}
                    mode="portfolio"
                  />
                  {outlineFC && (
                    <GeoJsonLayersVector
                      idPrefix="oversized-outlines"
                      map={map}
                      isMapLoaded={isMapLoaded}
                      geoJson={outlineFC}
                      layerConfigs={[
                        {
                          type: 'line',
                          layout: {'line-cap': 'round', 'line-join': 'round'},
                          paint: {
                            'line-color': SPLIT_ORIGINAL_OUTLINE_COLOR,
                            'line-width': 3,
                            'line-opacity': 0.9,
                          },
                        },
                      ]}
                    />
                  )}
                </>
              )}
            </React.Fragment>
          );
        }}
      </Map>
    </div>
  );
};

export default ManageFeaturesMap;
